import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { ServiceDog } from '../context/RegistryContext';

// Collection names
const COLLECTIONS = {
  DOGS: 'dogs',
  PENDING_DOGS: 'pendingDogs',
};

// Convert Firestore document to ServiceDog object
export const convertDocToServiceDog = (doc: QueryDocumentSnapshot<DocumentData>): ServiceDog => {
  const data = doc.data();
  return {
    id: doc.id,
    registryNumber: data.registryNumber,
    dogName: data.dogName,
    dogBreed: data.dogBreed,
    dogAge: data.dogAge,
    dogPhoto: data.dogPhoto,
    ownerName: data.ownerName,
    ownerAddress: data.ownerAddress,
    ownerPhone: data.ownerPhone,
    ownerEmail: data.ownerEmail,
    serviceType: data.serviceType,
    certificationFile: data.certificationFile || '',
    registrationDate: data.registrationDate.toDate().toISOString(),
    lastUpdated: data.lastUpdated.toDate().toISOString(),
    status: data.status,
  };
};

// Convert ServiceDog object to Firestore document
export const convertServiceDogToDoc = (dog: ServiceDog) => {
  return {
    registryNumber: dog.registryNumber,
    dogName: dog.dogName,
    dogBreed: dog.dogBreed,
    dogAge: dog.dogAge,
    dogPhoto: dog.dogPhoto,
    ownerName: dog.ownerName,
    ownerAddress: dog.ownerAddress,
    ownerPhone: dog.ownerPhone,
    ownerEmail: dog.ownerEmail,
    serviceType: dog.serviceType,
    certificationFile: dog.certificationFile || '',
    registrationDate: Timestamp.fromDate(new Date(dog.registrationDate)),
    lastUpdated: Timestamp.fromDate(new Date(dog.lastUpdated)),
    status: dog.status,
  };
};

// Add a new pending dog registration
export const addPendingDog = async (dog: Omit<ServiceDog, 'id' | 'registryNumber' | 'registrationDate' | 'lastUpdated' | 'status'>) => {
  try {
    const now = Timestamp.now();

    // Generate registry number with better error handling
    let registryNumber;
    try {
      registryNumber = await generateRegistryNumber();
    } catch (regError) {
      console.error("Error generating registry number:", regError);
      // Use timestamp-based fallback with new format
      const timestamp = Date.now();
      const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      registryNumber = `US-DOJ-1692001${timestamp.toString().slice(-4)}${randomSuffix.slice(0, 1)}`;
    }

    const newDog = {
      ...dog,
      registryNumber,
      registrationDate: now,
      lastUpdated: now,
      status: 'pending',
    };

    // Try to add to the pendingDogs collection
    try {
      console.log("Attempting to add dog to pendingDogs collection...");
      const docRef = await addDoc(collection(db, COLLECTIONS.PENDING_DOGS), newDog);
      console.log("Successfully added dog to pendingDogs collection with ID:", docRef.id);
      return {
        id: docRef.id,
        ...newDog,
        registrationDate: now.toDate().toISOString(),
        lastUpdated: now.toDate().toISOString(),
      } as ServiceDog;
    } catch (error) {
      console.error("Error adding to pendingDogs collection:", error);

      // If we can't add to pendingDogs, try adding to the dogs collection directly
      // This is a fallback for testing purposes only
      console.log("Attempting to add directly to dogs collection as a fallback...");
      const fallbackDog = {
        ...newDog,
        status: 'active', // Mark as active since we're adding directly to dogs collection
      };

      try {
        const docRef = await addDoc(collection(db, COLLECTIONS.DOGS), fallbackDog);
        console.log("Successfully added dog to dogs collection with ID:", docRef.id);
        return {
          id: docRef.id,
          ...fallbackDog,
          registrationDate: now.toDate().toISOString(),
          lastUpdated: now.toDate().toISOString(),
        } as ServiceDog;
      } catch (fallbackError) {
        console.error("Error adding to dogs collection as fallback:", fallbackError);
        // Create a local-only dog object with a temporary ID
        // This allows the UI flow to continue even if database operations fail
        const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        console.log("Creating temporary dog object with ID:", tempId);
        return {
          id: tempId,
          ...newDog,
          registrationDate: now.toDate().toISOString(),
          lastUpdated: now.toDate().toISOString(),
        } as ServiceDog;
      }
    }
  } catch (error) {
    console.error("Fatal error in addPendingDog:", error);
    throw error;
  }
};

// Approve a pending dog and move to active dogs collection
export const approveDog = async (dogId: string) => {
  // Get the pending dog
  const pendingDogRef = doc(db, COLLECTIONS.PENDING_DOGS, dogId);
  const pendingDogSnap = await getDoc(pendingDogRef);

  if (!pendingDogSnap.exists()) {
    throw new Error('Pending dog not found');
  }

  const pendingDogData = pendingDogSnap.data();

  // Update status and timestamp
  const now = Timestamp.now();
  const approvedDog = {
    ...pendingDogData,
    status: 'active',
    lastUpdated: now,
  };

  // Add to active dogs collection
  const activeDocRef = await addDoc(collection(db, COLLECTIONS.DOGS), approvedDog);

  // Delete from pending collection
  await deleteDoc(pendingDogRef);

  return {
    id: activeDocRef.id,
    ...approvedDog,
    registrationDate: pendingDogData.registrationDate.toDate().toISOString(),
    lastUpdated: now.toDate().toISOString(),
  } as ServiceDog;
};

// Deny a pending dog
export const denyDog = async (dogId: string) => {
  const pendingDogRef = doc(db, COLLECTIONS.PENDING_DOGS, dogId);
  await deleteDoc(pendingDogRef);
};

// Get all active dogs
export const getAllDogs = async (): Promise<ServiceDog[]> => {
  const dogsQuery = query(
    collection(db, COLLECTIONS.DOGS),
    orderBy('registrationDate', 'desc')
  );

  const querySnapshot = await getDocs(dogsQuery);
  return querySnapshot.docs.map(convertDocToServiceDog);
};

// Get all pending dogs
export const getPendingDogs = async (): Promise<ServiceDog[]> => {
  const pendingDogsQuery = query(
    collection(db, COLLECTIONS.PENDING_DOGS),
    orderBy('registrationDate', 'desc')
  );

  const querySnapshot = await getDocs(pendingDogsQuery);
  return querySnapshot.docs.map(convertDocToServiceDog);
};

// Get a dog by ID
export const getDogById = async (id: string): Promise<ServiceDog | null> => {
  try {
    // Check if this is a temporary ID (created when database operations fail)
    if (id.startsWith('temp-')) {
      console.log("Temporary ID detected, cannot retrieve from database:", id);
      // Return a placeholder dog object
      return {
        id: id,
        registryNumber: "US-DOJ-1692001TEMP" + id.substring(5, 9),
        dogName: "Registration Pending",
        dogBreed: "Unknown",
        dogAge: "Unknown",
        dogPhoto: "",
        ownerName: "Registration Pending",
        ownerAddress: "Registration Pending",
        ownerPhone: "Registration Pending",
        ownerEmail: "Registration Pending",
        serviceType: "Service Dog",
        certificationFile: "",
        registrationDate: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        status: 'pending'
      };
    }

    // Try active dogs first
    try {
      const activeDogRef = doc(db, COLLECTIONS.DOGS, id);
      const activeDogSnap = await getDoc(activeDogRef);

      if (activeDogSnap.exists()) {
        const data = activeDogSnap.data();
        return {
          id: activeDogSnap.id,
          ...data,
          registrationDate: data.registrationDate.toDate().toISOString(),
          lastUpdated: data.lastUpdated.toDate().toISOString(),
        } as ServiceDog;
      }
    } catch (error) {
      console.error("Error fetching from active dogs collection:", error);
      // Continue to try pending dogs
    }

    // Try pending dogs
    try {
      const pendingDogRef = doc(db, COLLECTIONS.PENDING_DOGS, id);
      const pendingDogSnap = await getDoc(pendingDogRef);

      if (pendingDogSnap.exists()) {
        const data = pendingDogSnap.data();
        return {
          id: pendingDogSnap.id,
          ...data,
          registrationDate: data.registrationDate.toDate().toISOString(),
          lastUpdated: data.lastUpdated.toDate().toISOString(),
        } as ServiceDog;
      }
    } catch (error) {
      console.error("Error fetching from pending dogs collection:", error);
    }

    console.log("Dog not found in either collection:", id);
    return null;
  } catch (error) {
    console.error("Fatal error in getDogById:", error);
    return null;
  }
};

// Get a dog by registry number
export const getDogByRegistryNumber = async (registryNumber: string): Promise<ServiceDog | null> => {
  try {
    // Check if this is a temporary registry number
    if (registryNumber.startsWith('US-DOJ-1692001TEMP') || registryNumber.startsWith('TEMP-')) {
      console.log("Temporary registry number detected, cannot retrieve from database:", registryNumber);
      // Return a placeholder dog object
      return {
        id: `temp-${Date.now()}`,
        registryNumber: registryNumber,
        dogName: "Registration Pending",
        dogBreed: "Unknown",
        dogAge: "Unknown",
        dogPhoto: "",
        ownerName: "Registration Pending",
        ownerAddress: "Registration Pending",
        ownerPhone: "Registration Pending",
        ownerEmail: "Registration Pending",
        serviceType: "Service Dog",
        certificationFile: "",
        registrationDate: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        status: 'pending'
      };
    }

    // Check active dogs
    try {
      const activeDogsQuery = query(
        collection(db, COLLECTIONS.DOGS),
        where('registryNumber', '==', registryNumber)
      );

      const activeQuerySnapshot = await getDocs(activeDogsQuery);

      if (!activeQuerySnapshot.empty) {
        return convertDocToServiceDog(activeQuerySnapshot.docs[0]);
      }
    } catch (error) {
      console.error("Error querying active dogs collection:", error);
      // Continue to try pending dogs
    }

    // Check pending dogs
    try {
      const pendingDogsQuery = query(
        collection(db, COLLECTIONS.PENDING_DOGS),
        where('registryNumber', '==', registryNumber)
      );

      const pendingQuerySnapshot = await getDocs(pendingDogsQuery);

      if (!pendingQuerySnapshot.empty) {
        return convertDocToServiceDog(pendingQuerySnapshot.docs[0]);
      }
    } catch (error) {
      console.error("Error querying pending dogs collection:", error);
    }

    console.log("Dog not found with registry number:", registryNumber);
    return null;
  } catch (error) {
    console.error("Fatal error in getDogByRegistryNumber:", error);
    return null;
  }
};

// Update a dog
export const updateDog = async (dog: ServiceDog): Promise<ServiceDog> => {
  // First, check if the dog exists and get its current status
  const originalDog = await getDogById(dog.id);
  if (!originalDog) {
    throw new Error(`Dog with ID ${dog.id} not found`);
  }

  const originalStatus = originalDog.status;
  const newStatus = dog.status;

  // Prepare update data
  const updateData = {
    ...convertServiceDogToDoc(dog),
    lastUpdated: serverTimestamp(),
  };

  // If status has changed, we need to move the document to a different collection
  if (originalStatus !== newStatus) {
    console.log(`Status changed from ${originalStatus} to ${newStatus}. Moving document to different collection.`);

    // Determine source and destination collections
    const sourceCollection = originalStatus === 'pending' ? COLLECTIONS.PENDING_DOGS : COLLECTIONS.DOGS;
    const destCollection = newStatus === 'pending' ? COLLECTIONS.PENDING_DOGS : COLLECTIONS.DOGS;

    // Get source document reference
    const sourceDocRef = doc(db, sourceCollection, dog.id);

    // Create new document in destination collection
    const destDocRef = doc(db, destCollection, dog.id);
    await setDoc(destDocRef, updateData);

    // Delete the document from the source collection
    await deleteDoc(sourceDocRef);

    // Get the updated document
    const updatedDogSnap = await getDoc(destDocRef);
    if (!updatedDogSnap.exists()) {
      throw new Error('Failed to update dog');
    }

    const data = updatedDogSnap.data();
    return {
      id: updatedDogSnap.id,
      ...data,
      registrationDate: data.registrationDate.toDate().toISOString(),
      lastUpdated: data.lastUpdated.toDate().toISOString(),
    } as ServiceDog;
  } else {
    // Status hasn't changed, just update the document in the same collection
    const collectionName = newStatus === 'pending' ? COLLECTIONS.PENDING_DOGS : COLLECTIONS.DOGS;
    const dogRef = doc(db, collectionName, dog.id);

    await updateDoc(dogRef, updateData);

    // Get the updated document
    const updatedDogSnap = await getDoc(dogRef);
    if (!updatedDogSnap.exists()) {
      throw new Error('Failed to update dog');
    }

    const data = updatedDogSnap.data();
    return {
      id: updatedDogSnap.id,
      ...data,
      registrationDate: data.registrationDate.toDate().toISOString(),
      lastUpdated: data.lastUpdated.toDate().toISOString(),
    } as ServiceDog;
  }
};

// Delete a dog
export const deleteDog = async (id: string, status: 'active' | 'pending' | 'revoked'): Promise<void> => {
  const collectionName = status === 'pending' ? COLLECTIONS.PENDING_DOGS : COLLECTIONS.DOGS;
  const dogRef = doc(db, collectionName, id);
  await deleteDoc(dogRef);
};

// Generate a unique registry number
export const generateRegistryNumber = async (): Promise<string> => {
  try {
    // Get all dogs to find the highest registry number
    const activeDogsQuery = query(collection(db, COLLECTIONS.DOGS));
    const pendingDogsQuery = query(collection(db, COLLECTIONS.PENDING_DOGS));

    try {
      const [activeDogsSnapshot, pendingDogsSnapshot] = await Promise.all([
        getDocs(activeDogsQuery),
        getDocs(pendingDogsQuery)
      ]);

      const allDogs = [
        ...activeDogsSnapshot.docs.map(doc => doc.data()),
        ...pendingDogsSnapshot.docs.map(doc => doc.data())
      ];

      // Find the highest registry number
      let highestNumber = 0;
      const prefix = "US-DOJ-1692001";

      allDogs.forEach(dog => {
        if (dog.registryNumber) {
          // Match both old format (SD-) and new format (US-DOJ-1692001)
          const oldFormatMatch = dog.registryNumber.match(/SD-(\d+)/);
          const newFormatMatch = dog.registryNumber.match(/US-DOJ-1692001(\d+)/);

          if (newFormatMatch && newFormatMatch[1]) {
            const num = parseInt(newFormatMatch[1], 10);
            if (num > highestNumber) {
              highestNumber = num;
            }
          } else if (oldFormatMatch && oldFormatMatch[1]) {
            // For backward compatibility with old format
            const num = parseInt(oldFormatMatch[1], 10);
            if (num > highestNumber) {
              highestNumber = num;
            }
          }
        }
      });

      // Generate the next registry number
      const nextNumber = highestNumber > 0 ? highestNumber + 1 : 1;
      return `${prefix}${nextNumber.toString().padStart(4, '0')}`;
    } catch (error) {
      console.error("Error fetching dogs for registry number:", error);
      // If we can't access the collections, generate a timestamp-based number
      // This is more reliable than random to avoid collisions
      const timestamp = Date.now();
      const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      return `US-DOJ-1692001${timestamp.toString().slice(-4)}${randomSuffix.slice(0, 1)}`;
    }
  } catch (error) {
    console.error("Fatal error in generateRegistryNumber:", error);
    // Fallback to timestamp-based number with random suffix
    const timestamp = Date.now();
    const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `US-DOJ-1692001${timestamp.toString().slice(-4)}${randomSuffix.slice(0, 1)}`;
  }
};